import { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Body1,
  Body1Strong,
  Button,
  Caption1,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  createTableColumn,
  makeStyles,
  tokens,
  useTableFeatures,
} from '@fluentui/react-components';
import { AddFilled, DocumentSearchRegular } from '@fluentui/react-icons';

import { usePersistedColumnSizing } from '@shared/hooks';
import { ToastContext } from '@shared/contexts/ToastContext.context';
import { useItemTableStyles } from '@modules/items';

import { EmptyState, SeverityCounts } from '../shared';
import { formatReviewDate, getBasisLabel, getReviewPath } from '../../helpers';
import { TReviewRegisterEntry, TReviewBasis } from '../../types';

const useStyles = makeStyles({
  muted: { color: tokens.colorNeutralForeground3 },
  cell: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  link: {
    display: 'block',
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
    textDecorationLine: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ':hover': { textDecorationLine: 'underline' },
  },
});

type TColumnId = 'review' | 'basis' | 'findings' | 'status' | 'started';

const COLUMN_IDS: TColumnId[] = ['review', 'basis', 'findings', 'status', 'started'];

const HEADER_LABEL: Record<TColumnId, string> = {
  review: 'Review',
  basis: 'Review basis',
  findings: 'Open findings',
  status: 'Status',
  started: 'Started',
};

const REVIEW_TABLE_ID = 'review-register-table';

const columnSizingParams = {
  autoFitColumns: false,
  columnSizingOptions: {
    review: { defaultWidth: 320, minWidth: 220 },
    basis: { defaultWidth: 240, minWidth: 160 },
    findings: { defaultWidth: 220, minWidth: 160 },
    status: { defaultWidth: 150, minWidth: 120 },
    started: { defaultWidth: 180, minWidth: 140 },
  },
};

type TProps = {
  reviews: TReviewRegisterEntry[];
  bases: TReviewBasis[];
  productId?: string;
  onStart: () => void;
};

export const ReviewsTable = ({ reviews, bases, productId, onStart }: TProps) => {
  const classes = useStyles();
  const tableClasses = useItemTableStyles();
  const { show } = useContext(ToastContext);

  const columns = useMemo(
    () => COLUMN_IDS.map((columnId) => createTableColumn<TReviewRegisterEntry>({ columnId })),
    []
  );
  const { columnSizing_unstable, getRows } = useTableFeatures(
    { columns, items: reviews, getRowId: (item) => item.id },
    [usePersistedColumnSizing(REVIEW_TABLE_ID, columnSizingParams)]
  );
  const rows = getRows();

  if (reviews.length === 0) {
    return (
      <Card className={tableClasses.card}>
        <EmptyState
          icon={<DocumentSearchRegular />}
          title="No reviews yet"
          message="Start a review to see findings here."
          action={
            <Button appearance="primary" icon={<AddFilled />} onClick={onStart}>
              New review
            </Button>
          }
        />
      </Card>
    );
  }

  return (
    <Card className={tableClasses.card}>
      <Table className={tableClasses.table} aria-label="Reviews" {...columnSizing_unstable.getTableProps()}>
        <TableHeader>
          <TableRow>
            {COLUMN_IDS.map((columnId) => (
              <TableHeaderCell key={columnId} {...columnSizing_unstable.getTableHeaderCellProps(columnId)}>
                <Body1Strong>{HEADER_LABEL[columnId]}</Body1Strong>
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody className={tableClasses.tableBody}>
          {rows.map(({ item }) => {
            const running = item.status === 'running';
            return (
              <TableRow key={item.id}>
                <TableCell {...columnSizing_unstable.getTableCellProps('review')}>
                  <div className={classes.cell}>
                    {item.access === 'restricted' ? (
                      <button
                        type="button"
                        className={`${classes.link} border-0 p-0 bg-transparent text-left cursor-pointer`}
                        onClick={() => show('warning', "You don't have permission to view this review.")}
                      >
                        {item.name}
                      </button>
                    ) : (
                      <Link to={getReviewPath(item.id, productId)} className={classes.link}>
                        {item.name}
                      </Link>
                    )}
                    <Caption1 className="truncate block">
                      <span className={classes.muted}>
                        {item.productName ? `${item.productName}, ${item.scopeLabel}` : item.scopeLabel}
                      </span>
                    </Caption1>
                  </div>
                </TableCell>
                <TableCell {...columnSizing_unstable.getTableCellProps('basis')}>
                  <Body1 className="truncate">
                    {item.reviewBasisIds.map((id) => getBasisLabel(bases, id)).join(', ') || '-'}
                  </Body1>
                </TableCell>
                <TableCell {...columnSizing_unstable.getTableCellProps('findings')}>
                  {running ? <Body1 className={classes.muted}>-</Body1> : <SeverityCounts summary={item.summary} />}
                </TableCell>
                <TableCell {...columnSizing_unstable.getTableCellProps('status')}>
                  {running ? (
                    <Badge appearance="tint" color="brand" size="medium">
                      Running
                    </Badge>
                  ) : (
                    <Badge appearance="tint" color="success" size="medium">
                      Completed
                    </Badge>
                  )}
                </TableCell>
                <TableCell {...columnSizing_unstable.getTableCellProps('started')}>
                  <div className="flex flex-col">
                    <Body1>{formatReviewDate(item.startedAt)}</Body1>
                    {item.startedBy ? <Caption1 className={classes.muted}>{item.startedBy}</Caption1> : null}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
};
